import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { EventDetailCapacity_event$key } from "./__generated__/EventDetailCapacity_event.graphql";

type EventDetailCapacityProps = {
  fragmentRef: EventDetailCapacity_event$key;
};

export const EventDetailCapacity = ({
  fragmentRef,
}: EventDetailCapacityProps) => {
  const data = useFragment(EventDetailCapacityFragment, fragmentRef);

  const { capacity, spotsAvailable, capacityType } = data;

  if (capacity == null || spotsAvailable == null) {
    return null;
  }

  const registered = capacity - spotsAvailable;
  const isGroupCapacity = capacityType === "GROUPS";

  const totalAttendees = isGroupCapacity
    ? data.eventSignUps?.edges?.reduce(
        (sum, signUp) => sum + (signUp?.node?.registrations?.count ?? 0),
        0
      ) ?? 0
    : null;

  return (
    <VStack gap={2} width="100%">
      <Text
        variant="mainSmallSemiBold"
        color="text-secondary"
      >{t`Capacity`}</Text>
      <HStack alignItems="center" gap={2}>
        <Text variant="mainBaseSemiBold" color="text-primary">
          {`${registered} / ${capacity}`}
        </Text>
        <Text variant="mainBaseMedium" color="text-primary">
          {isGroupCapacity ? t`Groups` : t`Attendees`}
        </Text>
        {isGroupCapacity && totalAttendees != null ? (
          <Text variant="mainBaseMedium" color="text-secondary">
            {t`${totalAttendees} Attendees`}
          </Text>
        ) : null}
        <Box flex={1} alignItems="flex-end">
          <Text variant="mainBaseMedium" color="text-main-primary">
            {t`${spotsAvailable} Spots Available`}
          </Text>
        </Box>
      </HStack>
    </VStack>
  );
};

export const EventDetailCapacityFragment = graphql`
  fragment EventDetailCapacity_event on Event {
    capacity
    spotsAvailable
    capacityType
    eventSignUps: signUps(waitlist: false) {
      edges {
        node {
          id
          registrations {
            count
          }
        }
      }
    }
  }
`;
